import { useState, useEffect, useContext } from 'react';

import {
  Grid,
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TableFooter,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Switch,
  FormControlLabel,
  TextField,
  Chip,
  Typography
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { BackdropComponent } from '../commons/backdrop/backdrop.component';
import { deleteProduct, getProducts, updateProduct } from '../../services/products.service';
import { getCategories } from '../../services/categories.service';
import { useStyles } from './products.styles';
import { ProductsContext } from '../../context/products.context';
import { has } from '../../services/summarize.service';

export const ProductsComponent = () => {

  const history = useHistory();

  const classes = useStyles();

  const { enqueueSnackbar } = useSnackbar();

  const productsContext = useContext(ProductsContext);

  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [display, setDisplay] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modal, setModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState();
  const [mobile, setMobile] = useState(true);

  const fetchProducts = async () => {
    const productsItems = await getProducts();
    productsContext.handleProducts(productsItems);
    setProducts(productsItems);
    setDisplay(productsItems);
    setLoading(false);
  }

  const fetchProductsByCategory = (categoryId) => {
    const productsItems = productsContext.getProductsByCategory(categoryId);
    setProducts(productsItems);
    setDisplay(productsItems);
    setLoading(false);
  }

  const fetchCategories = async () => {
    if (productsContext.categories.length === 0) {
      const categoriesItems = await getCategories();
      productsContext.handleCategories(categoriesItems);
      setCategories(categoriesItems);
    } else {
      setCategories(productsContext.categories);
    }
  }

  useEffect(() => {
    if (productsContext.products.length === 0) {
      fetchProducts();
    } else {
      setProducts(productsContext.products);
      setDisplay(productsContext.products);
      setLoading(false);
    }
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSearch = (event) => {
    const { value } = event.target;
    setDisplay(
      products.filter(product =>
        product.name.toLowerCase().includes(
          value.toLowerCase()
        )
      )
    );
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleEdit = (id) => {
    history.push(`/products/${id}/edit`);
  }

  const handleDialogDelete = (product) => {
    setSelectedProduct(product);
    setModal(true);
  }

  const handleClose = () => {
    setModal(false);
  }

  const handleDelete = async (productId) => {
    await deleteProduct(productId);
    setProducts(products.filter(product => product.id !== productId));
    setDisplay(display.filter(product => product.id !== productId));
    productsContext.handleProducts(productsContext.products.filter(product => product.id !== productId));
    setModal(false);
    enqueueSnackbar('Producto eliminado', {
      variant: 'success',
      resumeHideDuration: 3000,
      anchorOrigin:
        { horizontal: 'right', vertical: 'bottom' },
    });
  }

  const toggleMobile = () => {
    setMobile(!mobile);
  }

  const handleEnable = async (product) => {
    const updated = {
      ...product,
      enable: !product.enable
    }
    await updateProduct(product.id, updated);
    setProducts(products.map(p => p.id === product.id ? updated : p));
    setDisplay(display.map(p => p.id === product.id ? updated : p));
    productsContext.handleProducts(productsContext.products.map(p => p.id === product.id ? updated : p));
    enqueueSnackbar(`${product.name} ha sido ${updated.enable ? 'Habilitado' : 'Deshabilitado'}`, {
      variant: 'success',
      resumeHideDuration: 3000,
      anchorOrigin:
        { horizontal: 'right', vertical: 'bottom' },
    });
  }

  return (
    <Grid container justifyContent="center">
      <Grid item xs={12} lg={8}>
        <Grid container spacing={2}>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => history.push('/products/new')}
              disabled={!has('PRODUCTS::CREATE')}
            >
              Agregar producto
            </Button>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="search">Buscar</InputLabel>
              <OutlinedInput
                id="search"
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                }
                labelWidth={60}
                onChange={onSearch}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Grid container justifyContent="center" spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={mobile}
                      onChange={toggleMobile}
                      name="mobile"
                      color="primary"
                    />
                  }
                  label="Vista de celular"
                />
              </Grid>
              <Grid item xs={12}>
                {mobile ? (
                  <div className={classes.scrollable}>
                    {categories.sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0)).map((category) => (
                      <Chip
                        className={classes.chip}
                        key={category.id}
                        label={category.name}
                        onClick={() => fetchProductsByCategory(category.id)}
                      />
                    ))}
                  </div>
                ) : (
                  <Autocomplete
                    fullWidth
                    id="category"
                    options={categories}
                    getOptionLabel={(option) => option.name}
                    onChange={(event, value) => fetchProductsByCategory(value.id)}
                    style={{ width: '100%' }}
                    renderInput={(params) => <TextField {...params} label="Categorias" variant="outlined" />}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            {loading ? (
              <BackdropComponent />
            ) : (
              <TableContainer component={Paper}>
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Precio</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {display.length > 0 &&
                      display.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((row) => (
                          <TableRow key={row.id}>
                            <TableCell component="th" scope="row">
                              {row.name}
                            </TableCell>
                            <TableCell align="right">
                              ${row.price}
                            </TableCell>
                            <TableCell align="right">
                              <Grid container justifyContent="flex-end" spacing={2}>
                                <Grid item>
                                  <IconButton
                                    aria-label="enable"
                                    color="default"
                                    onClick={() => handleEnable(row)}
                                    disabled={!has('PRODUCTS::DELETE')}
                                  >
                                    {row.enable ? (
                                      <VisibilityOffIcon />
                                    ) : (
                                      <VisibilityIcon />
                                    )}
                                  </IconButton>
                                </Grid>
                                <Grid item>
                                  <IconButton
                                    aria-label="edit"
                                    color="default"
                                    onClick={() => handleEdit(row.id)}
                                    disabled={!has('PRODUCTS::EDIT')}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Grid>
                                <Grid item>
                                  <IconButton
                                    aria-label="delete"
                                    color="secondary"
                                    onClick={() => handleDialogDelete(row)}
                                    disabled={!has('PRODUCTS::DELETE')}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            </TableCell>
                          </TableRow>
                        ))}
                    {display.length === 0 && (
                      <Typography variant="body1" align="center">
                        No hay productos
                      </Typography>
                    )}
                  </TableBody>
                  <TableFooter>
                    <TablePagination
                      rowsPerPageOptions={[10, 20, 50]}
                      component="div"
                      count={display.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableFooter>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </Grid>
      </Grid>
      <Dialog
        open={modal}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          ¿Deseas eliminar el producto {selectedProduct?.name}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Si eliminas este producto no podras restablecerla
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancelar
          </Button>
          <Button onClick={() => handleDelete(selectedProduct?.id)} color="secondary" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  )
}